// HTTP client for the Perzo API. Cookie session + CSRF header on mutations, or,
// inside Shopify admin, a fresh App Bridge ID token on every request and no cookies.
// The client never sends tenant identifiers: the server derives the merchant
// from the authenticated session or the verified token.
import { assertCurrentIdentity, beginIdentityRequest } from './identity';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

type TokenProvider = () => Promise<string>;
let tokenProvider: TokenProvider | null = null;

/** Shopify App Home: authenticate every request with a token from this provider instead of cookies. */
export function setTokenProvider(provider: TokenProvider | null): void {
  tokenProvider = provider;
}

/** Shopify's signal that the ID token was stale: fetch a new one and retry once. */
const RETRY_HEADER = 'X-Shopify-Retry-Invalid-Session-Request';

async function providerToken(provider: TokenProvider): Promise<string> {
  try {
    return await provider();
  } catch (err) {
    throw new ApiError(401, 'shopify_session_unavailable', err instanceof Error ? err.message : 'Could not get a Shopify session token.');
  }
}

/**
 * fetch() with the caller's credentials: an explicit bearer (storefront), the App Home token provider,
 * or the cookie session with the CSRF header on mutations.
 */
async function authorizedFetch(url: string, init: { method: string; headers: Record<string, string>; body?: BodyInit; signal: AbortSignal }, options: RequestOptions): Promise<Response> {
  const provider = options.bearer ? null : tokenProvider;
  // Bearer (storefront) calls keep the page's own cookies: through the Shopify app proxy, a
  // password-protected storefront redirects requests without its password cookie to /password.
  // Same-origin only, so nothing is sent to a third party; storefront auth is the bearer itself.
  const credentials: RequestCredentials = options.credentials ?? (options.bearer ? 'same-origin' : provider ? 'omit' : 'include');
  const headers = { ...init.headers };
  if (options.bearer) {
    headers.Authorization = `Bearer ${options.bearer}`;
  } else if (!provider && MUTATING.has(init.method) && csrfToken && credentials !== 'omit') {
    headers['X-CSRF-Token'] = csrfToken;
  }
  for (let attempt = 0; ; attempt += 1) {
    const sent = provider ? { ...headers, Authorization: `Bearer ${await providerToken(provider)}` } : headers;
    const response = await fetch(url, { method: init.method, headers: sent, body: init.body, signal: init.signal, credentials });
    if (!provider || attempt > 0 || response.status !== 401 || response.headers.get(RETRY_HEADER) !== '1') return response;
  }
}

/** Filename from a Content-Disposition header (server-generated, ASCII). */
export function filenameFromDisposition(header: string | null): string | null {
  const match = header ? /filename="([^"]+)"/.exec(header) : null;
  return match?.[1] ?? null;
}

/** Authenticated binary GET (private previews and downloads in Shopify admin, where cookies are absent). */
export async function apiBlob(path: string, signal?: AbortSignal): Promise<{ blob: Blob; filename: string | null }> {
  const identityRequest = beginIdentityRequest(signal);
  try {
    let response: Response;
    try {
      response = await authorizedFetch(path, { method: 'GET', headers: {}, signal: identityRequest.signal }, {});
    } catch (err) {
      assertCurrentIdentity(identityRequest.epoch);
      if ((err instanceof DOMException && err.name === 'AbortError') || err instanceof ApiError) throw err;
      throw new ApiError(0, 'network_error', 'Could not reach the Perzo server. Check that it is running and try again.');
    }
    assertCurrentIdentity(identityRequest.epoch);
    if (!response.ok) throw new ApiError(response.status, `http_${response.status}`, fallbackMessage(response.status));
    const blob = await response.blob();
    assertCurrentIdentity(identityRequest.epoch);
    return { blob, filename: filenameFromDisposition(response.headers.get('content-disposition')) };
  } finally {
    identityRequest.done();
  }
}

/**
 * Authenticated binary POST (a file made from a request, like a ZIP of print files), read as a stream
 * so the caller can show the bytes received. JSON errors come back as ApiError with their message.
 */
export async function apiBlobPost(
  path: string,
  body: unknown,
  opts: { signal?: AbortSignal; onProgress?: (bytes: number) => void } = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const identityRequest = beginIdentityRequest(opts.signal);
  try {
    let response: Response;
    try {
      response = await authorizedFetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: identityRequest.signal }, {});
    } catch (err) {
      assertCurrentIdentity(identityRequest.epoch);
      if ((err instanceof DOMException && err.name === 'AbortError') || err instanceof ApiError) throw err;
      throw new ApiError(0, 'network_error', 'Could not reach the Perzo server. Check that it is running and try again.');
    }
    assertCurrentIdentity(identityRequest.epoch);
    if (!response.ok) {
      let message = fallbackMessage(response.status);
      let code = `http_${response.status}`;
      try {
        const err = ((await response.json()) as { error?: { code?: string; message?: string } }).error;
        if (err?.message) message = err.message;
        if (err?.code) code = err.code;
      } catch {
        /* not JSON */
      }
      throw new ApiError(response.status, code, message);
    }
    const chunks: Uint8Array[] = [];
    let received = 0;
    const reader = response.body?.getReader();
    if (reader) {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.byteLength;
        opts.onProgress?.(received);
      }
    }
    assertCurrentIdentity(identityRequest.epoch);
    const blob = reader ? new Blob(chunks as BlobPart[], { type: response.headers.get('content-type') ?? 'application/octet-stream' }) : await response.blob();
    return { blob, filename: filenameFromDisposition(response.headers.get('content-disposition')) };
  } finally {
    identityRequest.done();
  }
}

type UnauthorizedListener = (path: string) => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Subscribe to 401s from authenticated endpoints (session expired/revoked). */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Bearer capability for storefront routes. Disables cookie credentials. */
  bearer?: string;
  /** Skip the global 401 handler (used by auth bootstrap and login). */
  silentUnauthorized?: boolean;
  /** Override cookie behaviour (defaults: 'omit' with bearer, otherwise 'include'). */
  credentials?: RequestCredentials;
}

export function withQuery(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  if (!qs) return path;
  return path + (path.includes('?') ? '&' : '?') + qs;
}

const MUTATING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

function fallbackMessage(status: number): string {
  if (status === 400) return 'The request was rejected. Check the highlighted fields.';
  if (status === 401) return 'Your session has ended. Sign in again.';
  if (status === 403) return 'You do not have access to this resource.';
  if (status === 404) return 'This resource was not found. The endpoint may not be available yet.';
  if (status === 409) return 'This change conflicts with the current state. Reload and try again.';
  if (status === 413) return 'The file is too large.';
  if (status === 415) return 'This file type is not supported.';
  if (status === 422) return 'Some values are not valid.';
  if (status === 429) return 'Too many requests. Wait a moment and try again.';
  if (status >= 500) return 'The server failed to process this request.';
  return `Request failed (HTTP ${status}).`;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const identityRequest = beginIdentityRequest(options.signal);
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  let body: BodyInit | undefined;

  if (options.body instanceof FormData) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await authorizedFetch(withQuery(path, options.query), { method, headers, body, signal: identityRequest.signal }, options);
  } catch (err) {
    identityRequest.done();
    assertCurrentIdentity(identityRequest.epoch);
    if ((err instanceof DOMException && err.name === 'AbortError') || err instanceof ApiError) throw err;
    throw new ApiError(0, 'network_error', 'Could not reach the Perzo server. Check that it is running and try again.');
  }

  try {
    assertCurrentIdentity(identityRequest.epoch);
    if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  let payload: unknown = undefined;
  if (contentType.includes('application/json')) {
    try {
      payload = await response.json();
      assertCurrentIdentity(identityRequest.epoch);
    } catch {
      payload = undefined;
      assertCurrentIdentity(identityRequest.epoch);
    }
  }

  if (!response.ok) {
    const errorBody =
      payload && typeof payload === 'object' && 'error' in payload
        ? (payload as { error?: { code?: string; message?: string; details?: unknown; fields?: unknown } }).error
        : undefined;
    const error = new ApiError(
      response.status,
      errorBody?.code ?? `http_${response.status}`,
      errorBody?.message ?? fallbackMessage(response.status),
      errorBody?.details ?? errorBody?.fields,
    );
    if (response.status === 401 && !options.bearer && !options.silentUnauthorized) {
      assertCurrentIdentity(identityRequest.epoch);
      unauthorizedListeners.forEach((listener) => listener(path));
    }
    throw error;
  }

  if (payload === undefined && !contentType.includes('application/json')) {
    // Non-JSON success (should not happen for /api JSON routes). Surface it
    // instead of pretending an empty object is a valid resource.
    if (response.headers.get('content-length') === '0') return undefined as T;
    throw new ApiError(response.status, 'unexpected_response', `Unexpected ${contentType || 'unknown'} response from ${path}.`);
  }
    assertCurrentIdentity(identityRequest.epoch);
    return payload as T;
  } finally {
    identityRequest.done();
  }
}

export const http = {
  get: <T>(path: string, query?: Record<string, QueryValue>, opts: RequestOptions = {}) =>
    api<T>(path, { ...opts, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, opts: RequestOptions = {}) => api<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts: RequestOptions = {}) => api<T>(path, { ...opts, method: 'PATCH', body }),
  del: <T>(path: string, opts: RequestOptions = {}) => api<T>(path, { ...opts, method: 'DELETE' }),
};

/** Collection responses are `{ items: [] }`. Anything else is an integration bug. */
export function itemsOf<T>(value: { items?: T[] } | undefined | null): T[] {
  return Array.isArray(value?.items) ? value.items : [];
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unexpected error.';
}

/** Field-level messages when the API returns them (details: {field: message}). */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || !err.details || typeof err.details !== 'object') return {};
  const out: Record<string, string> = {};
  if (Array.isArray(err.details)) {
    for (const issue of err.details as Array<{ path?: unknown; field?: unknown; message?: unknown }>) {
      const key = Array.isArray(issue.path)
        ? issue.path.join('.')
        : typeof issue.path === 'string' && issue.path
          ? issue.path
          : typeof issue.field === 'string'
            ? issue.field
            : undefined;
      if (key && typeof issue.message === 'string') out[key] = issue.message;
    }
    return out;
  }
  for (const [key, value] of Object.entries(err.details as Record<string, unknown>)) {
    if (typeof value === 'string') out[key] = value;
    else if (Array.isArray(value) && typeof value[0] === 'string') out[key] = value[0];
  }
  return out;
}

export function newIdempotencyKey(): string {
  // randomUUID only exists in secure contexts; fall back to getRandomValues elsewhere.
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
